import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useNavEngagement from '../common/feature/nav-engagement';
import { BREAKPOINTS } from '../common/consts/app-keys.const';

// Served from /public rather than imported: at this size the reel has no
// business going through the bundler, and a plain URL lets the browser
// range-request it.
//
// Two cuts of the same reel. The reel needs to arrive faster than it plays or
// it stalls, and a stalled background is a frozen frame, so both are capped
// well under the connections they have to survive: 1080p at ~1.85 Mbps, 720p
// at ~1.05. Phones get the small one — they cannot resolve 1080p detail on a
// six-inch screen, and are likeliest to be on the connection that can't afford
// it.
const PUBLIC = process.env.PUBLIC_URL || '';

const CUTS = {
  desktop: {
    src: `${PUBLIC}/videos/squarebit_landing_reel.mp4`,
    poster: `${PUBLIC}/videos/squarebit_landing_reel_poster.jpg`
  },
  phoneLandscape: {
    src: `${PUBLIC}/videos/squarebit_landing_reel_720.mp4`,
    poster: `${PUBLIC}/videos/squarebit_landing_reel_poster.jpg`
  },
  portrait: {
    src: `${PUBLIC}/videos/squarebit_landing_reel_portrait.mp4`,
    poster: `${PUBLIC}/videos/squarebit_landing_reel_portrait_poster.jpg`
  }
} as const;

// The portrait cut is framed for the shape, not cropped down to it, so a phone
// held upright shows the reel as it was composed rather than the middle slice
// of a landscape frame.
const pickCut = () => {
  if (typeof window.matchMedia !== 'function') return CUTS.desktop;
  if (window.matchMedia(`(max-width: ${BREAKPOINTS.tablet - 1}px)`).matches) {
    return CUTS.portrait;
  }
  // Turned sideways a phone is short rather than narrow, so the width test
  // above misses it. It is still a small screen on mobile data, so it gets the
  // small landscape cut rather than the full desktop one.
  if (window.matchMedia('(max-height: 500px) and (orientation: landscape)').matches) {
    return CUTS.phoneLandscape;
  }
  return CUTS.desktop;
};

// No fill, no border: over a reel these are chrome, and chrome should not
// compete with the thing it sits on. The tap target stays a full 44px; only
// the visual weight goes. The shadow is what keeps them legible when the
// footage behind them turns bright.
const CONTROL_CLASS =
  'flex h-[44px] w-[44px] items-center justify-center rounded-full text-white/65 [filter:drop-shadow(0_1px_5px_rgba(0,0,0,0.7))] transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70';

// How long the controls stay up after they are called for.
const CONTROLS_LINGER_MS = 3500;

const PlayIcon = ({ playing }: { playing: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    {playing ? (
      <path d="M8 5h3v14H8zM13 5h3v14h-3z" />
    ) : (
      <path d="M7 4.5l13 7.5-13 7.5z" />
    )}
  </svg>
);

const SpeakerIcon = ({ muted }: { muted: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4v-5z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    {muted ? (
      <path
        d="M16 9.5l5 5m0-5l-5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    ) : (
      <path
        d="M15.5 9c1 .9 1.6 1.9 1.6 3s-.6 2.1-1.6 3M18 6.6C19.6 8 20.5 9.9 20.5 12s-.9 4-2.5 5.4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    )}
  </svg>
);

export const LandingPageContainer = () => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);

  // Decided once, at mount. Swapping src later would restart playback, which
  // is a worse trade than a rotated phone keeping the cut it started with.
  const [cut] = useState(pickCut);

  // Engaging the nav drops the reel to a quarter so the header reads against
  // it. The reel keeps playing throughout — it is only ever dimmed, never
  // swapped out or stopped.
  const isNavEngaged = useNavEngagement();

  // Controls announce themselves, then get out of the way, the way any video
  // player does. At rest the page is just the reel. Anything that looks like
  // reaching for them — a tap, the mouse moving, focus arriving — brings them
  // back, which also keeps the pause control reachable by keyboard.
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef<number>();

  const revealControls = useCallback(() => {
    setShowControls(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(
      () => setShowControls(false),
      CONTROLS_LINGER_MS
    );
  }, []);

  useEffect(() => {
    revealControls();
    document.addEventListener('pointerdown', revealControls);
    document.addEventListener('pointermove', revealControls);
    return () => {
      window.clearTimeout(hideTimer.current);
      document.removeEventListener('pointerdown', revealControls);
      document.removeEventListener('pointermove', revealControls);
    };
  }, [revealControls]);

  // Nothing on this page scrolls — the reel is fixed and there is nothing
  // under it. Left alone, phones scroll anyway: body's min-height of 100vh is
  // the viewport WITHOUT the URL bar, so the page ends up taller than what is
  // visible. See the .landing-locked rule for the rest of it.
  useEffect(() => {
    document.documentElement.classList.add('landing-locked');
    return () => document.documentElement.classList.remove('landing-locked');
  }, []);

  // Autoplay is a request, not a guarantee: browsers refuse it on their own
  // terms, and React sets `muted` as a property, which can land after the
  // attempt. Set it on the element and ask again, then believe whatever the
  // element reports back through onPlay/onPause.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    const attempt = video.play();
    if (attempt) attempt.catch(() => setIsPlaying(false));
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) video.play().catch(() => setIsPlaying(false));
    else video.pause();
  };

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;

    setIsMuted(!isMuted);
    // Unmuting counts as the user gesture browsers want, so a reel that never
    // started (autoplay refused) can begin here.
    if (isMuted && video.paused) video.play().catch(() => undefined);
  };

  return (
    // Fixed, at the bottom of the stack: this IS the page background, and the
    // whole app renders over it. Clicks pass straight through.
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-black">
      <video
        ref={videoRef}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
          isNavEngaged ? 'opacity-25' : 'opacity-100'
        }`}
        src={cut.src}
        poster={cut.poster}
        autoPlay
        muted={isMuted}
        loop
        playsInline
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        aria-label={t('landing.reelLabel')}
      >
        {t('landing.videoUnsupported')}
      </video>

      {/* Play/pause is the way back in when a browser refuses autoplay, and the
          pause a reel this long owes anyone who does not want it moving. Hidden
          is opacity only — the buttons stay focusable, and focus reveals them,
          so the pause control is never actually out of reach. */}
      <div
        onFocus={revealControls}
        className={`absolute bottom-[20px] right-[20px] flex items-center gap-[4px] transition-opacity duration-500 ${
          showControls ? 'pointer-events-auto opacity-100' : 'opacity-0'
        }`}
      >
        <button
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? t('landing.pause') : t('landing.play')}
          className={CONTROL_CLASS}
        >
          <PlayIcon playing={isPlaying} />
        </button>
        <button
          type="button"
          onClick={toggleSound}
          aria-label={isMuted ? t('landing.unmute') : t('landing.mute')}
          aria-pressed={!isMuted}
          className={CONTROL_CLASS}
        >
          <SpeakerIcon muted={isMuted} />
        </button>
      </div>
    </div>
  );
};

export default LandingPageContainer;
